import { useEffect, useMemo, useState } from "react";
import { Check, Copy, Download, KeyRound, RefreshCw, ShieldCheck, Trash2, UserPlus, Users } from "lucide-react";
import { SchoolClass } from "./ClassesView";

type StudentAccount = {
  id: string;
  student_code: string;
  full_name: string;
  class_id: string;
  student_id: string;
  active: boolean;
};

type TeacherAccount = {
  id: string;
  email: string;
  full_name: string;
  active: boolean;
  password_setup_required: boolean;
  created_at: string;
};

type NewCredential = { fullName: string; studentCode?: string; email?: string; password: string };

async function api(path: string, options?: RequestInit) {
  const response = await fetch(path, { credentials: "include", headers: { "Content-Type": "application/json", ...(options?.headers ?? {}) }, ...options });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error ?? "Une erreur est survenue");
  return body;
}

export function StudentAccountsView({ classes }: { classes: SchoolClass[] }) {
  const [classId, setClassId] = useState(classes[0]?.id ?? "");
  const [accounts, setAccounts] = useState<StudentAccount[]>([]);
  const [credentials, setCredentials] = useState<NewCredential[]>([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [copied, setCopied] = useState(false);
  const schoolClass = classes.find((item) => item.id === classId) ?? classes[0];

  async function load() {
    try { setAccounts((await api("/api/teacher/students")).students); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Chargement impossible"); }
  }
  useEffect(() => { void load(); }, []);
  useEffect(() => { if (!classes.some((item) => item.id === classId)) setClassId(classes[0]?.id ?? ""); }, [classes, classId]);

  const byStudent = useMemo(() => new Map(accounts.map((account) => [`${account.class_id}:${account.student_id}`, account])), [accounts]);
  const classAccounts = useMemo(() => schoolClass ? accounts.filter((account) => account.class_id === schoolClass.id) : [], [accounts, schoolClass]);

  async function createAccount(student: SchoolClass["students"][number]) {
    if (!schoolClass) return;
    setBusyId(student.id); setError("");
    try {
      const result = await api("/api/teacher/students", { method: "POST", body: JSON.stringify({ classId: schoolClass.id, studentId: student.id, firstName: student.firstName, lastName: student.lastName }) });
      setCredentials([result.account]);
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Création impossible"); }
    finally { setBusyId(""); }
  }

  async function toggle(account: StudentAccount) {
    await api(`/api/teacher/students/${account.id}`, { method: "PATCH", body: JSON.stringify({ active: !account.active }) });
    await load();
  }

  async function createMissingAccounts() {
    if (!schoolClass) return;
    setBusyId("bulk"); setError("");
    try {
      const result = await api("/api/teacher/students/bulk", {
        method: "POST",
        body: JSON.stringify({ classId: schoolClass.id, students: schoolClass.students.map((student) => ({ studentId: student.id, firstName: student.firstName, lastName: student.lastName })) }),
      });
      setCredentials(result.accounts);
      if (result.accounts.length === 0) setError("Tous les élèves de cette classe ont déjà un compte.");
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Création impossible"); }
    finally { setBusyId(""); }
  }

  async function resetAllPasswords() {
    if (!schoolClass) return;
    if (classAccounts.length === 0) {
      setError("Aucun compte élève à réinitialiser dans cette classe.");
      return;
    }
    if (!window.confirm(`Réinitialiser les mots de passe des ${classAccounts.length} comptes élèves de « ${schoolClass.name} » ?\n\nLes anciens mots de passe ne fonctionneront plus. Les nouveaux mots de passe ne seront affichés qu’une seule fois.`)) return;
    setBusyId("reset-all"); setError("");
    try {
      const result = await api("/api/teacher/students/reset-all", {
        method: "POST",
        body: JSON.stringify({ classId: schoolClass.id }),
      });
      setCredentials(result.accounts);
      if (result.accounts.length === 0) setError("Aucun compte élève à réinitialiser dans cette classe.");
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Réinitialisation impossible"); }
    finally { setBusyId(""); }
  }

  async function resetPassword(account: StudentAccount) {
    setBusyId(account.id); setError("");
    try { setCredentials([(await api(`/api/teacher/students/${account.id}/reset-password`, { method: "POST" })).account]); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Réinitialisation impossible"); }
    finally { setBusyId(""); }
  }

  async function copyCredentials() {
    if (!credentials.length) return;
    await navigator.clipboard.writeText(credentials.map((item) => `CompetenceO — ${item.fullName}\nIdentifiant : ${item.studentCode ?? item.email}\nMot de passe : ${item.password}`).join("\n\n"));
    setCopied(true); setTimeout(() => setCopied(false), 1600);
  }

  function exportCredentials() {
    const rows = [["Nom", "Code élève", "Mot de passe"], ...credentials.map((item) => [item.fullName, item.studentCode ?? "", item.password])];
    const csv = `\ufeff${rows.map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(";")).join("\n")}`;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    link.download = `comptes-${schoolClass?.name.toLowerCase().replace(/[^a-z0-9]+/gi, "-") ?? "eleves"}.csv`;
    link.click(); URL.revokeObjectURL(link.href);
  }

  return <main className="management-page accounts-page">
    <div className="management-heading"><div><span className="eyebrow">Accès personnels</span><h1>Comptes élèves</h1></div><div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}><button className="secondary" disabled={!schoolClass || classAccounts.length === 0 || busyId === "bulk" || busyId === "reset-all"} onClick={resetAllPasswords}><RefreshCw size={17} />{busyId === "reset-all" ? "Réinitialisation…" : "Réinitialiser tous les mots de passe"}</button><button className="primary compact" disabled={!schoolClass || busyId === "bulk" || busyId === "reset-all"} onClick={createMissingAccounts}><Users size={17} />{busyId === "bulk" ? "Création…" : "Créer les comptes manquants"}</button></div></div>
    {credentials.length > 0 && <section className="credentials-card"><div><KeyRound size={22} /><div><strong>{credentials.length === 1 ? `Identifiants de ${credentials[0].fullName}` : `${credentials.length} identifiants générés`}</strong><span>Enregistre-les maintenant : les mots de passe ne seront plus affichés.</span></div></div><div className="credentials-table">{credentials.map((item) => <div key={`${item.studentCode}-${item.fullName}`}><strong>{item.fullName}</strong><code>{item.studentCode}</code><code>{item.password}</code></div>)}</div><button className="primary compact" onClick={copyCredentials}>{copied ? <Check size={16} /> : <Copy size={16} />}{copied ? "Copié" : "Copier"}</button><button className="secondary" onClick={exportCredentials}><Download size={16} /> Exporter CSV</button><button className="secondary" onClick={() => setCredentials([])}>Fermer</button></section>}
    {error && <p className="account-error">{error}</p>}
    <label className="account-class-picker"><span>Classe</span><select value={schoolClass?.id ?? ""} onChange={(event) => setClassId(event.target.value)}>{classes.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
    <section className="account-list">
      {schoolClass?.students.map((student) => { const account = byStudent.get(`${schoolClass.id}:${student.id}`); return <article key={student.id}><div className="account-avatar">{student.firstName[0]}{student.lastName[0]}</div><div><strong>{student.firstName} {student.lastName}</strong><span>{account ? `Code : ${account.student_code}` : "Aucun compte"}</span></div>{account ? <div className="account-actions"><button className="secondary" disabled={busyId === account.id || busyId === "reset-all"} onClick={() => resetPassword(account)}><RefreshCw size={15} /> Nouveau mot de passe</button><button className={account.active ? "secondary" : "primary compact"} disabled={busyId === "reset-all"} onClick={() => toggle(account)}>{account.active ? "Désactiver" : "Réactiver"}</button></div> : <button className="primary compact" disabled={busyId === student.id || busyId === "reset-all"} onClick={() => createAccount(student)}><UserPlus size={16} />{busyId === student.id ? "Création…" : "Créer le compte"}</button>}</article>; })}
      {!schoolClass && <div className="management-empty">Crée d’abord une classe et importe ses élèves.</div>}
    </section>
  </main>;
}

export function AdminTeachersView() {
  const [teachers, setTeachers] = useState<TeacherAccount[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [credentials, setCredentials] = useState<NewCredential | null>(null);
  const [mailConfigured, setMailConfigured] = useState(false);
  const [busyId, setBusyId] = useState("");

  async function load() {
    try {
      const result = await api("/api/admin/teachers");
      setTeachers(result.teachers);
      setMailConfigured(Boolean(result.mailConfigured));
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Chargement impossible"); }
  }
  useEffect(() => { void load(); }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault(); setError(""); setNotice(""); setCredentials(null);
    try {
      const result = await api("/api/admin/teachers", {
        method: "POST",
        body: JSON.stringify({ fullName, email, ...(mailConfigured ? {} : { password }) }),
      });
      if (result.invitationSent) setNotice(`Compte créé. Invitation envoyée à ${result.teacher.email}.`);
      setFullName(""); setEmail(""); setPassword(""); setShowForm(false); await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Création impossible");
      await load();
    }
  }

  async function toggle(teacher: TeacherAccount) {
    await api(`/api/admin/teachers/${teacher.id}`, { method: "PATCH", body: JSON.stringify({ active: !teacher.active }) });
    await load();
  }

  async function resetPassword(teacher: TeacherAccount) {
    setError(""); setNotice(""); setCredentials(null); setBusyId(teacher.id);
    try {
      const result = await api(`/api/admin/teachers/${teacher.id}/reset-password`, { method: "POST" });
      if (result.account) setCredentials(result.account);
      else if (result.emailSent) setNotice(result.purpose === "invite"
        ? `Invitation renvoyée à ${result.email}.`
        : `E-mail de réinitialisation envoyé à ${result.email}.`);
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Réinitialisation impossible"); }
    finally { setBusyId(""); }
  }

  async function deleteTeacher(teacher: TeacherAccount) {
    setError(""); setNotice(""); setCredentials(null);
    const confirmation = window.prompt(
      `SUPPRESSION DÉFINITIVE de ${teacher.full_name}.\n\nToutes ses classes, élèves, activités, compétences, séances, plans, photos, sauvegardes, QCM et résultats seront supprimés.\n\nRecopie exactement son adresse e-mail pour confirmer :\n${teacher.email}`,
    );
    if (confirmation === null) return;
    if (confirmation.trim().toLowerCase() !== teacher.email.trim().toLowerCase()) {
      setError("Suppression annulée : l’adresse e-mail saisie ne correspond pas.");
      return;
    }
    if (!window.confirm(`Dernière confirmation : supprimer définitivement ${teacher.full_name} et toutes ses données ? Cette action est irréversible.`)) return;

    setBusyId(`delete:${teacher.id}`);
    try {
      const result = await api(`/api/admin/teachers/${teacher.id}`, {
        method: "DELETE",
        body: JSON.stringify({ confirmation }),
      });
      setNotice(`Le compte de ${result.deleted?.fullName ?? teacher.full_name} et toutes ses données ont été supprimés.`);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Suppression impossible");
    } finally {
      setBusyId("");
    }
  }

  return <main className="management-page accounts-page">
    <div className="management-heading"><div><span className="eyebrow">Administration</span><h1>Comptes professeurs</h1></div><button className="primary" onClick={() => setShowForm((value) => !value)}><UserPlus size={17} /> Nouveau professeur</button></div>
    {notice && <p className="account-success"><Check size={16} /> {notice}</p>}
    {credentials && <section className="credentials-card"><div><KeyRound size={22} /><div><strong>Nouveau mot de passe de {credentials.fullName}</strong><span>Mode provisoire : configure l’envoi e-mail pour utiliser les liens sécurisés.</span></div></div><div className="credentials-table"><div><strong>{credentials.fullName}</strong><code>{credentials.email}</code><code>{credentials.password}</code></div></div><button className="secondary" onClick={() => setCredentials(null)}>Fermer</button></section>}
    {showForm && <form className="management-form teacher-account-form" onSubmit={submit}><div className="form-title"><strong>{mailConfigured ? "Inviter un professeur" : "Créer un compte professeur"}</strong><button type="button" onClick={() => setShowForm(false)}>Annuler</button></div><label><span>Nom complet</span><input value={fullName} onChange={(event) => setFullName(event.target.value)} required /></label><label><span>Email</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>{!mailConfigured && <label><span>Mot de passe provisoire</span><input type="password" minLength={10} value={password} onChange={(event) => setPassword(event.target.value)} required /></label>}<button className="primary form-submit"><ShieldCheck size={16} /> {mailConfigured ? "Créer et envoyer l’invitation" : "Créer le compte"}</button></form>}
    {error && <p className="account-error">{error}</p>}
    <section className="teacher-list">{teachers.map((teacher) => {
      const status = !teacher.active ? "Désactivé" : teacher.password_setup_required ? "Invitation en attente" : "Actif";
      const deleting = busyId === `delete:${teacher.id}`;
      return <article key={teacher.id}><div className="account-avatar"><Users size={19} /></div><div><strong>{teacher.full_name}</strong><span>{teacher.email}</span></div><i className={!teacher.active || teacher.password_setup_required ? "account-disabled" : "account-active"}>{status}</i><div className="account-actions"><button className="secondary" disabled={busyId === teacher.id || deleting} onClick={() => resetPassword(teacher)}><RefreshCw size={15} /> {busyId === teacher.id ? "Envoi…" : mailConfigured ? teacher.password_setup_required ? "Renvoyer l’invitation" : "Réinitialiser le mot de passe" : "Nouveau mot de passe"}</button><button className="secondary" disabled={deleting} onClick={() => toggle(teacher)}>{teacher.active ? "Désactiver" : "Réactiver"}</button><button className="secondary" aria-label={`Supprimer définitivement le compte de ${teacher.full_name}`} title="Supprimer définitivement" style={{ color: "#b42318", borderColor: "#efb5af", width: 42, minWidth: 42, height: 38, padding: 0 }} disabled={deleting} onClick={() => deleteTeacher(teacher)}><Trash2 size={18} /></button></div></article>;
    })}{teachers.length === 0 && <div className="management-empty">Aucun autre professeur.</div>}</section>
  </main>;
}

export function PasswordChangeForm({ compact = false, onDone }: { compact?: boolean; onDone?: () => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  async function submit(event: React.FormEvent) {
    event.preventDefault(); setError(""); setMessage("");
    if (newPassword !== confirmation) return setError("Les deux nouveaux mots de passe sont différents.");
    try {
      await api("/api/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) });
      setCurrentPassword(""); setNewPassword(""); setConfirmation(""); setMessage("Mot de passe modifié."); onDone?.();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Modification impossible"); }
  }
  return <main className={compact ? "password-panel compact-password" : "management-page password-page"}><form className="password-form" onSubmit={submit}><div><span className="eyebrow">Sécurité</span><h1>Changer mon mot de passe</h1><p>Utilise au moins 10 caractères.</p></div><label><span>Mot de passe actuel</span><input type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required /></label><label><span>Nouveau mot de passe</span><input type="password" autoComplete="new-password" minLength={10} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} required /></label><label><span>Confirmer le nouveau mot de passe</span><input type="password" autoComplete="new-password" minLength={10} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required /></label>{error && <p className="account-error">{error}</p>}{message && <p className="account-success"><Check size={16} /> {message}</p>}<button className="primary"><ShieldCheck size={16} /> Enregistrer le nouveau mot de passe</button></form></main>;
}
